import {
  boolean,
  decimal,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { contacts } from "./contact"

export const subscriptionStatusEnum = pgEnum("subscription_status_enum", ["Active", "Disabled"])

// Lookup Tables

export const subscriptionPlans = pgTable("subscription_plan", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 100 }).notNull().unique(),
  description: text("description"),
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  durationMonths: integer("duration_months"),
  stripePriceId: varchar("stripe_price_id", { length: 255 }),
})

export const reportFees = pgTable("report_fees", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 100 }).notNull().unique(),
  description: text("description"),
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  durationMonths: integer("duration_months"),
})

// Core Tables

export const userTypes = pgEnum("user_types", ["individual", "professional"])

export const users = pgTable("user", {
  // This ID comes from Cognito and is not a UUID.
  id: varchar("id", { length: 255 }).primaryKey(),
  contactId: uuid("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" })
    .unique(),
  email: varchar("email", { length: 255 }).notNull().unique(),
  userType: userTypes("user_type").notNull().default("individual"),
  personalBrandLogoKey: varchar("personal_brand_logo_key", { length: 512 }),
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }).unique(),
  lastLoginAt: timestamp("last_login_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  isActive: boolean("is_active").default(true),
})

export const userSubscriptions = pgTable("user_subscription", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: varchar("user_id", { length: 255 })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  subscriptionPlanId: uuid("subscription_plan_id")
    .notNull()
    .references(() => subscriptionPlans.id),
  subscriptionStatus: subscriptionStatusEnum("subscription_status_enum")
    .default("Active")
    .notNull(),
  stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
  stripePriceId: varchar("stripe_price_id", { length: 255 }),
  currentPeriodStart: timestamp("current_period_start"),
  currentPeriodEnd: timestamp("current_period_end"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end").default(false),
  trialEnd: timestamp("trial_end"),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"),
  autoRenew: boolean("auto_renew").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

export const regionEnum = pgEnum("region", ["us", "ca"])
export const transactionTypeEnum = pgEnum("transaction_type", ["subscription", "report", "refund"])

export const transactions = pgTable("transaction", {
  id: uuid("id").primaryKey().defaultRandom(),
  userSubscriptionId: uuid("user_subscription_id")
    .notNull()
    .references(() => userSubscriptions.id, { onDelete: "cascade" }),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  transactionDate: timestamp("transaction_date").defaultNow().notNull(),
  description: text("description"),
  paymentGatewayRef: varchar("payment_gateway_ref", { length: 255 }),
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
  region: regionEnum("region"),
  transactionType: transactionTypeEnum("transaction_type"),
  reportId: uuid("report_id"),
})

export const userAnnualReportSpending = pgTable(
  "user_annual_report_spending",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: varchar("user_id", { length: 255 })
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    year: integer("year").notNull(),
    totalSpentCents: integer("total_spent_cents").notNull().default(0),
    capReachedAt: timestamp("cap_reached_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    uniqueUserYear: {
      columns: [table.userId, table.year],
      name: "unique_user_year",
    },
  }),
)

export const reportPrices = pgTable("report_price", {
  id: uuid("id").primaryKey().defaultRandom(),
  reportType: varchar("report_type", { length: 255 }).notNull().unique(),
  priceCents: integer("price_cents").notNull(),
  stripePriceId: varchar("stripe_price_id", { length: 255 }),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})
